import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { ErrorsList } from '../errors'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { useToast } from '../toast'
import { TemplateBodyPreview } from './template-body-preview'
import { TemplateBodyInput } from './template-body-input'
import { TemplateBindingsEditor } from './template-bindings-editor'
import { TemplateServerErrors, templateServerErrors, toFieldErrors } from './template-server-errors'
import { whatsappTemplateApi } from 'src/services/whatsapp/api'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'
import { WhatsappTemplateForm, TWhatsappTemplateForm } from 'src/services/whatsapp/forms'
import { useTemplateBindings } from 'src/services/whatsapp/use-template-bindings'
import {
  templateCategoryEnum,
  templateLanguageOptions,
  templateStatusEnum,
  WhatsappTemplate,
} from 'src/services/whatsapp/models'
import { InfoHint } from 'src/components/info-hint'

interface EditTemplateModalProps {
  // Only ever opened for a `rejected` or `draft` row — null means closed.
  template: WhatsappTemplate | null
  onClose: () => void
  // Unlocks the check-in survey link in the variable picker.
  hasReportingProperties?: boolean
}

const EditTemplateModalContent: React.FC<{
  template: WhatsappTemplate
  onClose: () => void
  hasReportingProperties?: boolean
}> = ({ template, onClose, hasReportingProperties }) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { form, createFormFieldChangeHandler } = useTnForm<TWhatsappTemplateForm>()
  // A draft saves without resubmitting and is submitted explicitly; a
  // rejected row is resubmitted by the save itself.
  const isDraft = template.status === templateStatusEnum.DRAFT

  // Prefill from the template being edited. The surrounding FormProvider is
  // keyed on the template id, so this runs once per template opened.
  useEffect(() => {
    // The name isn't editable, but the form's required-name validator still
    // runs on submit, so it must carry the template's name.
    createFormFieldChangeHandler(form._name)(template.name)
    createFormFieldChangeHandler(form.category)(template.category)
    createFormFieldChangeHandler(form.language)(template.language)
    createFormFieldChangeHandler(form.body)(template.body)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const bodyValue = form.body.value ?? ''
  const { variables, bindings, setSource, setField, allMapped, buildBindings } =
    useTemplateBindings(bodyValue, template.variableBindings)

  const categoryOptions = [
    { value: templateCategoryEnum.UTILITY, label: t('whatsappTemplates.categoryUtility') },
    { value: templateCategoryEnum.MARKETING, label: t('whatsappTemplates.categoryMarketing') },
    {
      value: templateCategoryEnum.AUTHENTICATION,
      label: t('whatsappTemplates.categoryAuthentication'),
    },
  ]
  const languageOptions = templateLanguageOptions.map((code) => ({
    value: code,
    label: t(`whatsappTemplates.language_${code}`),
  }))

  // Field errors from a rejected save or `/submit/`, shown inline.
  const [serverErrors, setServerErrors] = useState<TemplateServerErrors>({})

  const {
    mutate: saveTemplate,
    isPending,
    variables: pendingSubmit,
  } = useMutation({
    mutationFn: async (submit: boolean) => {
      const saved = await whatsappTemplateApi.update({
        id: template.id,
        category: form.category.value ?? templateCategoryEnum.UTILITY,
        language: form.language.value ?? 'en',
        body: form.body.value ?? '',
        variableBindings: buildBindings(),
      })
      return isDraft && submit ? whatsappTemplateApi.csc.submit(template.id) : saved
    },
    onMutate: () => setServerErrors({}),
    onSuccess: (_template, submit) => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      const successKey = !isDraft
        ? 'whatsappTemplates.editSuccess'
        : submit
        ? 'whatsappTemplates.createSuccess'
        : 'whatsappTemplates.draftSaved'
      toast({ description: t(successKey), type: 'success' })
      onClose()
    },
    onError: (error) => {
      // The draft edit itself may have saved before `/submit/` refused it.
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      setServerErrors(templateServerErrors(error))
      toast({ description: t('whatsappTemplates.editError'), type: 'error' })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid || !allMapped) return
    saveTemplate(true)
  }

  const handleSaveDraft = () => {
    form.language.validate()
    if (!form.language.isValid) return
    saveTemplate(false)
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t(isDraft ? 'whatsappTemplates.editDraftTitle' : 'whatsappTemplates.editTitle')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-template-form">
        <div>
          <Input
            label={t('whatsappTemplates.formNameLabel')}
            value={template.name}
            disabled
            data-testid="template-edit-name"
          />
          <p className="mt-1 text-xs text-text-tertiary">
            {t('whatsappTemplates.nameImmutableHint')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <SelectWrapper
              label={t('whatsappTemplates.formCategoryLabel')}
              onBlur={() => form.category.validate()}
            >
              <CustomSelect
                options={categoryOptions}
                values={categoryOptions.filter((o) => o.value === form.category.value)}
                onChange={(values) => createFormFieldChangeHandler(form.category)(values[0]?.value)}
              />
            </SelectWrapper>
            <ErrorsList errors={form.category.errors} />
          </div>

          <div>
            <SelectWrapper
              label={t('whatsappTemplates.formLanguageLabel')}
              onBlur={() => form.language.validate()}
            >
              <CustomSelect
                options={languageOptions}
                values={languageOptions.filter((o) => o.value === form.language.value)}
                onChange={(values) => createFormFieldChangeHandler(form.language)(values[0]?.value)}
              />
            </SelectWrapper>
            <ErrorsList errors={form.language.errors} />
            <ErrorsList errors={toFieldErrors(serverErrors.language)} />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('whatsappTemplates.formBodyLabel')}
            <InfoHint text={t('whatsappTemplates.variablesHint')} className="ml-1.5" />
          </label>
          <TemplateBodyInput
            value={form.body.value ?? ''}
            onChange={(value) => createFormFieldChangeHandler(form.body)(value)}
            onBlur={() => form.body.validate()}
            placeholder={form.body.placeholder}
            hasReportingProperties={hasReportingProperties}
            data-testid="template-edit-body"
          />
          <ErrorsList errors={form.body.errors} />
          <ErrorsList errors={toFieldErrors(serverErrors.body)} />
        </div>

        {form.body.value ? (
          <div className="rounded-md border border-border bg-surface-hover p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-placeholder">
              {t('whatsappTemplates.previewLabel')}
            </p>
            <TemplateBodyPreview body={form.body.value} />
          </div>
        ) : null}

        <TemplateBindingsEditor
          variables={variables}
          bindings={bindings}
          onSourceChange={setSource}
          onFieldChange={setField}
        />
        <ErrorsList errors={toFieldErrors(serverErrors.variableBindings)} />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {isDraft ? (
            <Button
              type="button"
              variant="secondary"
              disabled={isPending}
              onClick={handleSaveDraft}
              data-testid="save-draft-edit-template"
            >
              {isPending && !pendingSubmit
                ? t('whatsappTemplates.savingDraft')
                : t('whatsappTemplates.saveDraft')}
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={isPending || !allMapped}
            data-testid="submit-edit-template"
          >
            {isPending && pendingSubmit
              ? t('whatsappTemplates.submitting')
              : t(isDraft ? 'whatsappTemplates.submitForApproval' : 'whatsappTemplates.resubmit')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  template,
  onClose,
  hasReportingProperties,
}) => {
  if (!template) return null
  // Keyed on the template id so switching between rejected templates (or
  // reopening the same one after a cancel) always remounts with fresh state.
  return (
    <FormProvider formClass={WhatsappTemplateForm} key={template.id}>
      <EditTemplateModalContent
        template={template}
        onClose={onClose}
        hasReportingProperties={hasReportingProperties}
      />
    </FormProvider>
  )
}
